import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { CreditCard, CheckCircle2, ExternalLink, ArrowRight, Zap, Shield, TestTube, Sparkles } from "lucide-react";

const STEPS = [
    {
        icon: TestTube,
        title: "You're in test mode already",
        body: "Any card ending 4242 will process a full test donation flow. No real money moves. Perfect for showing your board or letting a colleague make a demo gift.",
        cta: null,
    },
    {
        icon: Shield,
        title: "Claim your Stripe account",
        body: "One click opens Stripe's hosted onboarding. Add your legal business info, bank details, and a signer. Takes 3–5 minutes.",
        cta: { label: "Open Stripe onboarding", href: "onboarding" },
    },
    {
        icon: Zap,
        title: "Flip to live mode",
        body: "Once your Stripe dashboard shows 'Payments enabled', copy your live keys into RescueLink's environment. We'll swap immediately — no downtime.",
        cta: { label: "How to swap keys", href: "docs" },
    },
    {
        icon: Sparkles,
        title: "Watch donations land",
        body: "Every payment lands in your Stripe balance instantly and reflects in the RescueLink Global Fund total on the home page.",
        cta: null,
    },
];

const FAQ = [
    { q: "Do I need a Stripe account to accept donations right now?", a: "No. RescueLink is already running against a shared test-mode account. Real donations require you to complete Stripe onboarding and then swap to live keys." },
    { q: "Which countries can accept via Stripe?", a: "Stripe supports 46+ countries. Check the up-to-date list at stripe.com/global. Most Asia, EU, North America, and Australia jurisdictions are covered." },
    { q: "How long between donation and settlement?", a: "Standard Stripe payout is T+2 business days. You can enable instant payouts for a small fee once volume exceeds $200/week." },
    { q: "What fees does Stripe charge?", a: "2.2% + $0.30 per card charge for verified nonprofits (regular rate 2.9% + $0.30). Stripe waives its own fees for eligible 501(c)(3)s under Stripe for Nonprofits — apply once your dashboard is live." },
    { q: "How do I test the checkout right now?", a: "Head to /donate, use card 4242 4242 4242 4242, any future date, any CVC. You'll see the success screen and the transaction lands in the test Stripe dashboard." },
    { q: "What about recurring donations?", a: "Toggle 'Give monthly' on the donation page. Stripe creates a subscription — donors can cancel from a link in their receipt email." },
];

export default function StripeSetup() {
    const [info, setInfo] = useState(null);
    const [expanded, setExpanded] = useState(0);

    useEffect(() => {
        api.get("/stripe/onboarding").then((r) => setInfo(r.data));
    }, []);

    const status = info?.mode === "live" ? "live" : "test";

    return (
        <div data-testid="stripe-setup-page">
            <div className="bg-[var(--rl-cream)] border-b border-[var(--rl-border)]">
                <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-14">
                    <div className="eyebrow mb-3 inline-flex items-center gap-2"><CreditCard className="w-3.5 h-3.5" /> Stripe Wingman</div>
                    <h1 className="font-serif text-4xl lg:text-6xl tracking-tight max-w-3xl">Stripe, made painless.<br />Your rescue on real payments in 5 minutes.</h1>
                    <p className="mt-4 text-[var(--rl-text-2)] max-w-2xl">Most rescues stall out on the payments step. This page walks you through every screen, with click-to-copy commands and screenshots that match what you'll actually see in Stripe.</p>

                    <div className="mt-6 inline-flex items-center gap-3 rounded-full border border-[var(--rl-border-strong)] bg-white/70 px-4 py-2 text-sm" data-testid="stripe-status">
                        <span className={`w-2 h-2 rounded-full ${status === "live" ? "bg-[var(--rl-success)]" : "bg-[#e2a900]"}`}></span>
                        <span className="font-semibold">{status === "live" ? "Live mode" : "Test mode"}</span>
                        <span className="text-[var(--rl-text-3)]">•</span>
                        <span className="text-[var(--rl-text-2)] font-mono text-xs">{info?.key_prefix || "—"}{info?.account_id ? ` · ${info.account_id}` : ""}</span>
                    </div>
                </div>
            </div>

            {/* Steps */}
            <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-12">
                <div className="grid md:grid-cols-2 gap-5">
                    {STEPS.map((s, i) => {
                        const Icon = s.icon;
                        return (
                            <div key={i} className="card-warm rounded-2xl p-6" data-testid={`stripe-step-${i}`}>
                                <div className="flex items-start gap-4">
                                    <div className="w-11 h-11 rounded-full bg-[var(--rl-primary)] text-white flex items-center justify-center shrink-0">
                                        <Icon className="w-5 h-5" />
                                    </div>
                                    <div className="flex-1">
                                        <div className="eyebrow text-[var(--rl-text-3)]">Step {i + 1}</div>
                                        <h3 className="font-serif text-2xl mt-1">{s.title}</h3>
                                        <p className="text-sm text-[var(--rl-text-2)] mt-2 leading-relaxed">{s.body}</p>
                                        {s.cta?.href === "onboarding" && info?.onboarding_url && (
                                            <a href={info.onboarding_url} target="_blank" rel="noreferrer">
                                                <Button className="btn-primary rounded-full h-10 mt-4" data-testid="stripe-onboarding-btn">
                                                    <ExternalLink className="w-3.5 h-3.5 mr-1.5" /> {s.cta.label}
                                                </Button>
                                            </a>
                                        )}
                                        {s.cta?.href === "docs" && (
                                            <a href="https://stripe.com/docs/keys" target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 mt-4 text-sm font-semibold text-[var(--rl-primary)]" data-testid="stripe-docs-link">
                                                {s.cta.label} <ArrowRight className="w-3.5 h-3.5" />
                                            </a>
                                        )}
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </section>

            {/* Test now callout */}
            <section className="max-w-[1400px] mx-auto px-6 lg:px-10 pb-12">
                <div className="card-urgent rounded-2xl p-8 grid lg:grid-cols-[1fr_auto] gap-6 items-center">
                    <div>
                        <div className="eyebrow text-[var(--rl-primary)] mb-2">Try it now</div>
                        <h3 className="font-serif text-3xl">Run a test donation without leaving this tab.</h3>
                        <p className="mt-2 text-[var(--rl-text-2)] max-w-xl">Card: <code className="bg-[var(--rl-cream)] px-2 py-0.5 rounded font-mono">4242 4242 4242 4242</code> · any future date · any 3-digit CVC · any ZIP.</p>
                    </div>
                    <Link to="/donate"><Button className="btn-primary rounded-full h-11 px-6" data-testid="test-donation-btn">Open donation page</Button></Link>
                </div>
            </section>

            {/* FAQ */}
            <section className="max-w-[900px] mx-auto px-6 lg:px-10 pb-20">
                <h2 className="font-serif text-3xl mb-6">Common questions</h2>
                <div className="space-y-3">
                    {FAQ.map((f, i) => {
                        const open = expanded === i;
                        return (
                            <div key={i} className="card-warm rounded-xl overflow-hidden" data-testid={`faq-${i}`}>
                                <button className="w-full text-left px-5 py-4 flex items-center gap-4"
                                        onClick={() => setExpanded(open ? -1 : i)}>
                                    <CheckCircle2 className={`w-4 h-4 shrink-0 ${open ? "text-[var(--rl-primary)]" : "text-[var(--rl-text-3)]"}`} />
                                    <span className="font-semibold text-sm">{f.q}</span>
                                    <span className={`ml-auto text-[var(--rl-text-3)] transition-transform ${open ? "rotate-45" : ""}`}>+</span>
                                </button>
                                {open && (
                                    <div className="px-5 pb-5 pl-14 text-sm text-[var(--rl-text-2)]">{f.a}</div>
                                )}
                            </div>
                        );
                    })}
                </div>
            </section>
        </div>
    );
}
